import { useState } from 'react';
import useRecipeV2Api from './useRecipeV2Api';

export type RecipeGovernanceAction = 'submit' | 'approve' | 'reject' | 'rollback';

interface UseRecipeGovernanceOptions {
  recipeId: number;
  refreshVersions: () => Promise<void>;
  onGovernanceMutated: () => Promise<void>;
  decisionReasonRequiredError: string;
}

/** Owns request/reconciliation UX; the API is authoritative for lifecycle and authorization. */
const useRecipeGovernance = ({
  recipeId,
  refreshVersions,
  onGovernanceMutated,
  decisionReasonRequiredError,
}: UseRecipeGovernanceOptions) => {
  const api = useRecipeV2Api();
  const [action, setAction] = useState<RecipeGovernanceAction | null>(null);
  const [reason, setReason] = useState('');
  const [rollbackVersionNumber, setRollbackVersionNumber] = useState<number | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const open = (nextAction: RecipeGovernanceAction, versionNumber?: number) => {
    setReason('');
    setError(null);
    setAction(nextAction);
    setRollbackVersionNumber(versionNumber ?? null);
  };
  const close = () => {
    if (isBusy) return;
    setAction(null);
    setRollbackVersionNumber(null);
  };
  const run = async () => {
    if (!action) return;
    if ((action === 'approve' || action === 'reject') && !reason.trim()) {
      setError(decisionReasonRequiredError);
      return;
    }
    setIsBusy(true);
    setError(null);
    setFeedback(null);
    try {
      if (action === 'submit') {
        await api.submitRecipe(recipeId, reason.trim() || undefined);
        setFeedback('submit');
      } else if (action === 'approve') {
        await api.approveRecipe(recipeId, { comment: reason.trim() });
        setFeedback('approve');
      } else if (action === 'reject') {
        await api.rejectRecipe(recipeId, { comment: reason.trim() });
        setFeedback('reject');
      } else if (rollbackVersionNumber != null) {
        const version = await api.rollbackVersion(recipeId, rollbackVersionNumber, reason.trim() || undefined);
        setFeedback(`rollback:${version}`);
      }
      setAction(null);
      setReason('');
      setRollbackVersionNumber(null);
      await Promise.all([refreshVersions(), onGovernanceMutated()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Governance action failed.');
    } finally {
      setIsBusy(false);
    }
  };
  return { action, reason, setReason, isBusy, error, feedback, open, close, run };
};

export default useRecipeGovernance;
